import type { LanguageDropdownItem, SupportedLocales } from './types.ts';
import type { IntlShape } from 'react-intl';

// @ts-expect-error
import { defaultMessages } from '../../features/languageData';

const DEFAULT_LOCALE = 'en-GB';
const DEFAULT_LANG = 'en';

interface LocaleMap {
    [key: string]: string;
}

const supportedLocaleMap: LocaleMap = {
    bg: 'bg-BG',
    'bg-BG': 'bg-BG',
    cs: 'cs-CZ',
    'cs-CZ': 'cs-CZ',
    da: 'da-DK',
    'da-DK': 'da-DK',
    de: 'de-DE',
    'de-DE': 'de-DE',
    el: 'el-GR',
    'el-GR': 'el-GR',
    en: 'en-GB',
    'en-GB': 'en-GB',
    es: 'es-ES',
    'es-ES': 'es-ES',
    et: 'et-EE',
    'et-EE': 'et-EE',
    fi: 'fi-FI',
    'fi-FI': 'fi-FI',
    fr: 'fr-FR',
    'fr-FR': 'fr-FR',
    hr: 'hr-HR',
    'hr-HR': 'hr-HR',
    hu: 'hu-HU',
    'hu-HU': 'hu-HU',
    it: 'it-IT',
    'it-IT': 'it-IT',
    ko: 'ko-KR',
    'ko-KR': 'ko-KR',
    lt: 'lt-LT',
    'lt-LT': 'lt-LT',
    lv: 'lv-LV',
    'lv-LV': 'lv-LV',
    nb: 'nb-NO',
    'nb-NO': 'nb-NO',
    nl: 'nl-NL',
    'nl-NL': 'nl-NL',
    no: 'nb-NO',
    pl: 'pl-PL',
    'pl-PL': 'pl-PL',
    pt: 'pt-PT',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    ro: 'ro-RO',
    'ro-RO': 'ro-RO',
    sk: 'sk-SK',
    'sk-SK': 'sk-SK',
    sl: 'sl-SI',
    'sl-SI': 'sl-SI',
    sv: 'sv-SE',
    'sv-SE': 'sv-SE',
};

const LOCALE_ID_PREFIX = 'intl-msg:companySettings.language.locale.';

const sortLocales = (locales: SupportedLocales, messagesOfLocale: { [key: string]: string }) => {
    return locales
        .map(locale => {
            return {
                locale: locale,
                displayName: messagesOfLocale[`${LOCALE_ID_PREFIX}${locale}`],
            };
        })
        .sort((a, b) => {
            const displayNameA = a.displayName || '';
            const displayNameB = b.displayName || '';

            /* eslint-disable no-console */
            console.assert(Boolean(displayNameA), `translation missing for key: ${LOCALE_ID_PREFIX}${a.locale}`);
            console.assert(Boolean(displayNameB), `translation missing for key: ${LOCALE_ID_PREFIX}${b.locale}`);

            return displayNameA.toLocaleLowerCase().localeCompare(displayNameB.toLocaleLowerCase());
        })
        .map(item => item.locale);
};

const getLanguageDropDownItems = (tenant: string | undefined, intl: IntlShape, _selectedLocale?: string) => {
    const options: LanguageDropdownItem[] = [];
    const locales = tenant ? [...new Set(Object.values(supportedLocaleMap))] : [];
    const sortedLocales = sortLocales(locales, defaultMessages);
    sortedLocales.forEach((locale: string) => {
        options.push({
            id: locale,
            label: intl.formatMessage({ id: `intl-msg:companySettings.language.locale.${locale}` }),
        });
    });
    return options;
};

const extractLanguageForGlossar = (value: string | undefined | null): string => {
    const extractedLanguage = extractLanguage(value);
    if (extractedLanguage === 'ko') {
        // Korean is not supported in the glossar
        return DEFAULT_LANG;
    } else if (extractedLanguage) {
        return extractedLanguage;
    } else {
        return DEFAULT_LANG;
    }
};

const extractLanguage = (value: string | undefined | null): string =>
    (value ?? DEFAULT_LOCALE).split('-')[0] ?? DEFAULT_LANG;
const getSupportedLocale = (preferredLocale: string): string => supportedLocaleMap[preferredLocale] ?? DEFAULT_LOCALE;

export {
    DEFAULT_LANG,
    DEFAULT_LOCALE,
    extractLanguage,
    supportedLocaleMap,
    getSupportedLocale,
    extractLanguageForGlossar,
    getLanguageDropDownItems,
};
